import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import TOKENS from '../data/tokens';
import EntrySplashScreen from './EntrySplashScreen';
import IntroOverlay from './IntroOverlay';
import Navbar from './Navbar';
import SlideMenu from './SlideMenu';
import HeroSection from './HeroSection';
import VideoSection from './VideoSection';
import TheEveningCard from './TheEveningCard';
import RSVPPublicModule from './RSVPPublicModule';
import CountdownStrip from './CountdownStrip';
import SponsorsCard from './SponsorsCard';
import ExploreEntrySection from './ExploreEntrySection';
import MessageForm from './MessageForm';
import FooterSection from './FooterSection';
import PreviewBanner from './PreviewBanner';
import MaintenanceScreen from './MaintenanceScreen';
import { isPhase2Released, isMaintenanceMode } from '../utils/featureGates';

/**
 * PublicSite - Guest-facing event landing page
 * 
 * LAYOUT ORDER (LOCKED - MOBILE-FIRST):
 * 1. Event Identity (HeroSection) - what / when / where
 * 2. Video Slot (VideoSection) - container preserved, content held
 * 3. RSVP Module - full inline RSVP with YES/NO flow
 * 4. RSVP Deadline + Event Countdown strip - premium card styling
 * 5. Sponsors Card - recognition card (secondary importance)
 * 6. "Your Private Guide" Explore Carousel - Travel, Things to Do, Calendar
 * 7. Footer
 * 
 * NAVIGATION MODEL:
 * - Main page = focused experience (identity, RSVP)
 * - Secondary content = separate pages via Explore entries + hamburger menu
 * 
 * DISABLED COMPONENTS (retained for inventory):
 * - RegistrationForm (Save-the-Date) at ./RegistrationForm.jsx
 * - Video content (slot preserved, content held for future)
 * 
 * FUTURE ITEMS:
 * - Oscar: Future roadmap item, likely Google-grounded
 * - Sponsor backend wiring: Will pull from Finance/sponsor entries
 */

// Feature flag checks
const SHOW_EXPLORE = isPhase2Released();

// Maintenance mode wrapper - renders MaintenanceScreen if in maintenance mode
const MaintenanceGate = ({ children }) => {
  if (isMaintenanceMode()) {
    return <MaintenanceScreen />;
  }
  return children;
};

const PublicSiteContent = () => {
  const location = useLocation();
  // Entry splash DISABLED — controlled preview, bypass always
  const [hasEntered] = useState(true);
  const [introComplete, setIntroComplete] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const scrollRef = useRef(null);
  
  // Persist entry — no longer needed but keep for back-compat
  const handleEnter = () => {};

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = () => setIsScrolled(el.scrollTop > 50);
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, [introComplete]);

  // Auto-scroll to RSVP section when arriving with ?token= in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('token')) {
      const timer = setTimeout(() => {
        const rsvpEl = document.getElementById('rsvp-welcome-header');
        if (rsvpEl) rsvpEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [location.search]);

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={{ 
      width: '100%', 
      height: '100vh', 
      position: 'relative', 
      overflow: 'hidden',
    }}>
      {/* Preview Domain Warning Banner */}
      <PreviewBanner />
      
      {/* Entry Splash Screen - DISABLED (controlled preview) */}
      {/* {!hasEntered && <EntrySplashScreen onEnter={handleEnter} />} */}
      
      {/* Main Site Content */}
      <div style={{ 
        opacity: 1,
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
      }}>
        {/* Floating logo + hamburger — no background container */}
        <Navbar onMenuToggle={() => setMenuOpen(!menuOpen)} />
        <SlideMenu open={menuOpen} onClose={() => setMenuOpen(false)} scrollTo={scrollTo} />

        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <div className="site-column" style={{ display: 'flex', flexDirection: 'column', paddingTop: 64 }}>

            {/* 1. Event Identity — existing top content, unchanged */}
            <HeroSection />

            {/* 2. Video (landscape) */}
            <VideoSection />

            {/* 3. The Evening — venue, date/time, attire */}
            <TheEveningCard />

            {/* 4. RSVP Module */}
            <RSVPPublicModule />

            {/* Remaining sections — below RSVP, order unchanged */}
            <CountdownStrip />
            <SponsorsCard />
            <ExploreEntrySection />
            <MessageForm />
            <FooterSection scrollTo={scrollTo} />
          </div>
        </div>
        
      </div>
    </div>
  );
};

// Main export wraps content in maintenance gate
const PublicSite = () => (
  <MaintenanceGate>
    <PublicSiteContent />
  </MaintenanceGate>
);

export default PublicSite;
